// csv-parser.js — minimal CSV parser for DropPin contact import.
//
// v0.39.2 — initial. Handles the formats you actually get from Google
//           Contacts, iCloud Contacts and a spreadsheet of subbie
//           names + numbers:
//             - Comma OR tab delimited (auto-detected from header row)
//             - Quoted fields with embedded commas (RFC 4180-ish)
//             - "" escape inside quoted fields
//             - Mixed line endings (\r\n, \n)
//             - Empty trailing rows skipped
//
// Header-row mapping: case-insensitive substring match against a list
// of common column names per field. So "Name", "Full Name",
// "Contact Name" all map to .name; "Mobile", "Phone", "Cell" all map
// to .phone; etc.
//
// Returns: an array of {name, email, phone, company, trade, notes}
// matching the shape POST /api/contacts expects. Rows missing a name
// are dropped (no useful contact without one).

const COLUMN_HINTS = {
  name:    ['name', 'full name', 'contact', 'display name', 'first name'],
  email:   ['email', 'e-mail', 'mail'],
  phone:   ['mobile', 'phone', 'cell', 'tel', 'number'],
  company: ['company', 'organisation', 'organization', 'business', 'employer'],
  trade:   ['trade', 'role', 'title', 'job', 'occupation', 'category'],
  notes:   ['note', 'comment', 'remark', 'description'],
};

const TRADE_HINTS = [
  { needle: 'electric',  trade: 'electrical' },
  { needle: 'sparky',    trade: 'electrical' },
  { needle: 'plumb',     trade: 'plumbing'   },
  { needle: 'carpenter', trade: 'carpentry'  },
  { needle: 'chippy',    trade: 'carpentry'  },
  { needle: 'paint',     trade: 'painting'   },
  { needle: 'tile',      trade: 'tiling'     },
  { needle: 'tiler',     trade: 'tiling'     },
  { needle: 'hvac',      trade: 'hvac'       },
  { needle: 'air con',   trade: 'hvac'       },
  { needle: 'aircon',    trade: 'hvac'       },
  { needle: 'mechanic',  trade: 'hvac'       },
  { needle: 'roof',      trade: 'roofing'    },
  { needle: 'glaz',      trade: 'glazing'    },
  { needle: 'window',    trade: 'glazing'    },
  { needle: 'floor',     trade: 'flooring'   },
];

function guessTrade(text) {
  if (!text) return null;
  const t = String(text).toLowerCase();
  for (const h of TRADE_HINTS) {
    if (t.includes(h.needle)) return h.trade;
  }
  return null;
}

// Tokenise a single line into fields. Honours RFC-4180-style quoting
// (double-quote wraps; embedded "" is a literal ").
function splitRow(line, delim) {
  const out = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else { cur += ch; }
    } else {
      if (ch === '"') { inQuotes = true; }
      else if (ch === delim) { out.push(cur); cur = ''; }
      else { cur += ch; }
    }
  }
  out.push(cur);
  return out.map(s => s.trim());
}

function pickDelimiter(headerLine) {
  // If the line has tabs and they outnumber commas, use TAB.
  const tabs = (headerLine.match(/\t/g) || []).length;
  const commas = (headerLine.match(/,/g) || []).length;
  return tabs > commas ? '\t' : ',';
}

function mapHeader(headers) {
  // For each known field (name/email/phone/company/trade/notes), find
  // the first header whose lowercased text contains one of the hint
  // substrings. Returns an object { name: 2, email: 5, ... } of
  // column indexes. -1 if not present.
  const idx = {};
  for (const field of Object.keys(COLUMN_HINTS)) {
    idx[field] = -1;
    for (let i = 0; i < headers.length; i++) {
      const h = String(headers[i] || '').toLowerCase().trim();
      if (!h) continue;
      if (COLUMN_HINTS[field].some(hint => h.includes(hint))) {
        idx[field] = i;
        break;
      }
    }
  }
  return idx;
}

/**
 * Parse a CSV/TSV string into an array of contact records.
 * @param {string} text  raw CSV contents
 * @returns {Array<{name, email, phone, company, trade, notes}>}
 */
export function parseCsv(text) {
  if (!text || typeof text !== 'string') return [];
  const lines = text.split(/\r?\n/).filter(l => l.length > 0);
  if (!lines.length) return [];

  const delim = pickDelimiter(lines[0]);
  const headers = splitRow(lines[0], delim);
  const idx = mapHeader(headers);

  // If no Name column matched, treat the first column as the name.
  if (idx.name === -1) idx.name = 0;

  const out = [];
  for (let r = 1; r < lines.length; r++) {
    const cells = splitRow(lines[r], delim);
    if (!cells.length || cells.every(c => !c.trim())) continue;
    const get = (k) => (idx[k] !== -1 && idx[k] < cells.length) ? cells[idx[k]].trim() : '';
    const name = get('name');
    if (!name) continue;
    const tradeRaw = get('trade');
    const tradeKey = guessTrade(tradeRaw) || guessTrade(get('company'));
    out.push({
      name,
      email:   get('email')   || null,
      phone:   get('phone')   || null,
      company: get('company') || null,
      // If the CSV trade column already matches a known key, keep it;
      // otherwise the guess from TRADE_HINTS substring match wins.
      trade:   tradeKey || (tradeRaw ? tradeRaw.toLowerCase() : null),
      notes:   get('notes')   || null,
    });
  }
  return out;
}
